import { COVER_NOTICE_SWEEP } from '@nest-recruit/shared';
import { formatDate, formatDateTime } from '../../../../lib/format';

// Review R28: when the stand-in really hears about a cover. N-69 ("you are covering for…") is sent by a sweep at five past each hour
// from 08:05 to 20:05 Bangladesh time (`COVER_NOTICE_SWEEP`, the server's schedule), to every stand-in whose cover is in force and who
// hasn't been told. So the sentence on the form says when that next run is, in the organisation's zone, never the browser's.

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** A cover is never longer than this; the search for the next run stops here. */
const MAX_DAYS_AHEAD = 400;

interface WallDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

function wallDateOf(instant: number, zone: string): WallDate {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((entry) => entry.type === type)?.value ?? 0);
  return { year: part('year'), month: part('month'), day: part('day') };
}

/** How far the zone's wall clock is ahead of UTC at this instant. */
function zoneOffsetMs(instant: number, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((entry) => entry.type === type)?.value ?? 0);
  const wall = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
  return wall - Math.floor(instant / 1000) * 1000;
}

/** The instant the zone's clock shows this date and time. */
function instantOf(date: WallDate, hour: number, minute: number, zone: string): number {
  const asUtc = Date.UTC(date.year, date.month - 1, date.day, hour, minute);
  return asUtc - zoneOffsetMs(asUtc, zone);
}

function addDays(date: WallDate, days: number): WallDate {
  const moved = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: moved.getUTCDate() };
}

function sameDay(a: WallDate, b: WallDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

/**
 * The first sweep that will tell the stand-in: the first run at or after both the save and the start, while the cover is still in
 * force. Null when the cover ends before any run would see it.
 */
export function coverNoticeAt(startsAt: string, endsAt: string, now: Date): Date | null {
  const { zone, minute, fromHour, toHour } = COVER_NOTICE_SWEEP;
  const from = Math.max(Date.parse(startsAt), now.getTime());
  const until = Date.parse(endsAt);
  const firstDay = wallDateOf(from, zone);
  for (let offset = 0; offset <= MAX_DAYS_AHEAD; offset += 1) {
    const day = addDays(firstDay, offset);
    for (let hour = fromHour; hour <= toHour; hour += 1) {
      const run = instantOf(day, hour, minute, zone);
      if (run < from) continue;
      return run <= until ? new Date(run) : null;
    }
  }
  return null;
}

/** "Arif Rahman gets an email …": when N-69 really goes, in plain words. */
export function coverNoticeWording(standInName: string, startsAt: string, endsAt: string, now: Date): string {
  const notice = coverNoticeAt(startsAt, endsAt, now);
  const { zone } = COVER_NOTICE_SWEEP;
  if (notice === null) return `${standInName} won't get an email: the cover ends before the next morning's notice goes out.`;
  const noticeDay = wallDateOf(notice.getTime(), zone);
  const today = wallDateOf(now.getTime(), zone);
  const startDay = wallDateOf(Date.parse(startsAt), zone);
  const startsLater = Date.parse(startsAt) > now.getTime() && !sameDay(startDay, today);
  if (startsLater && sameDay(noticeDay, startDay)) {
    return `${standInName} gets an email the morning the cover starts, ${formatDate(startsAt)}.`;
  }
  if (sameDay(noticeDay, today)) {
    return notice.getTime() - now.getTime() <= HOUR_MS
      ? `${standInName} gets an email within the hour.`
      : `${standInName} gets an email at ${formatDateTime(notice)}.`;
  }
  if (sameDay(noticeDay, addDays(today, 1)) && notice.getTime() - now.getTime() < DAY_MS) {
    return `${standInName} gets an email tomorrow morning.`;
  }
  return `${standInName} gets an email on the morning of ${formatDate(notice)}.`;
}
